import { Theme, alpha } from "@mui/material";

declare module "@mui/material/Button" {
  interface ButtonPropsVariantOverrides {
    GreyOutlined: true;
    dashed: true;
    white: true;
  }
}

declare module "@mui/material/TextField" {
  interface TextFieldPropsColorOverrides {
    grey: true;
  }
}

const components = (theme: Theme): any => {
  const { mode, error, divider, action, grey, primary, text, success, background } = theme.palette;
  const isLight = mode === "light";

  // Brand-tinted focus ring.
  const focusRing = `0 0 0 3px ${alpha(primary.main, 0.22)}`;
  // Soft elevation for cards / popovers.
  // In dark mode an inset top highlight reveals the card edge softly,
  // and bottom shadow keeps depth without the harsh "black hole" look.
  const cardShadow = isLight
    ? "0 1px 2px rgba(12, 10, 9, 0.04), 0 1px 1px rgba(12, 10, 9, 0.02)"
    : "0 1px 2px rgba(0, 0, 0, 0.35), inset 0 1px 0 rgba(231, 229, 228, 0.03)";
  const popoverShadow = isLight
    ? "0 12px 28px -10px rgba(12, 10, 9, 0.14), 0 6px 12px -6px rgba(12, 10, 9, 0.08)"
    : "0 16px 32px -10px rgba(0, 0, 0, 0.55), 0 8px 16px -6px rgba(0, 0, 0, 0.35), inset 0 1px 0 rgba(231, 229, 228, 0.04)";
  const dialogShadow = isLight
    ? "0 32px 56px -20px rgba(12, 10, 9, 0.22), 0 12px 24px -10px rgba(12, 10, 9, 0.1)"
    : "0 32px 56px -20px rgba(0, 0, 0, 0.65), 0 12px 24px -10px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(231, 229, 228, 0.04)";

  return {
    MuiCssBaseline: {
      styleOverrides: {
        "*": {
          margin: 0,
          padding: 0,
          boxSizing: "border-box",
        },
        html: {
          width: "100%",
          height: "100%",
          WebkitOverflowScrolling: "touch",
          MozOsxFontSmoothing: "grayscale",
          WebkitFontSmoothing: "antialiased",
          textRendering: "optimizeLegibility",
        },
        body: {
          width: "100%",
          height: "100%",
          letterSpacing: "-0.005em",
          fontFeatureSettings: "'cv11', 'ss01', 'ss03'",
          backgroundColor: background.default,
          color: text.primary,
        },
        a: {
          textDecoration: "none",
          color: primary.main,
          transition: "color 140ms ease",
          "&:hover": { color: primary.dark },
        },
        "::selection": {
          backgroundColor: alpha(primary.main, 0.22),
        },
        input: {
          "&[type=number]": {
            MozAppearance: "textfield",
            "&::-webkit-outer-spin-button": {
              margin: 0,
              WebkitAppearance: "none",
            },
            "&::-webkit-inner-spin-button": {
              margin: 0,
              WebkitAppearance: "none",
            },
          },
        },
        "#root": { width: "100%", height: "100%" },
        "#nprogress .bar": {
          zIndex: "2000 !important",
          backgroundColor: primary.main,
          height: 2,
        },
        // Scrollbars: minimal, theme-aware.
        "*::-webkit-scrollbar": { width: 10, height: 10 },
        "*::-webkit-scrollbar-thumb": {
          backgroundColor: alpha(text.primary, isLight ? 0.14 : 0.2),
          borderRadius: 999,
          border: `2px solid ${background.default}`,
        },
        "*::-webkit-scrollbar-thumb:hover": {
          backgroundColor: alpha(text.primary, isLight ? 0.26 : 0.32),
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: { fontSize: 13, fontWeight: 600 },
        fallback: { height: "75%", width: "75%" },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
        disableRipple: false,
      },
      styleOverrides: {
        root: {
          textTransform: "none",
          borderRadius: 10,
          color: text.primary,
          boxShadow: "none",
          padding: "0.45rem 1rem",
          fontSize: 13,
          fontWeight: 550,
          letterSpacing: "-0.005em",
          minWidth: 0,
          height: 38,
          transition:
            "background-color 150ms ease, color 150ms ease, border-color 150ms ease, box-shadow 150ms ease, transform 150ms ease",
          "&.Mui-focusVisible": { boxShadow: focusRing },
          "&.Mui-disabled": {
            backgroundColor: action.disabledBackground,
            color: text.disabled,
            borderColor: "transparent",
          },
          "&.MuiButton-containedError": {
            backgroundColor: error.main,
            color: "#fff",
            "&:hover": { backgroundColor: error.dark },
          },
          "&.MuiButton-containedSuccess": {
            backgroundColor: success.main,
            color: "#fff",
            "&:hover": { backgroundColor: success.dark },
          },
          "& .MuiButton-startIcon .MuiSvgIcon-root": { fontSize: 16 },
          "& .MuiButton-endIcon .MuiSvgIcon-root": { fontSize: 16 },
        },
        outlinedPrimary: {
          borderColor: divider,
          color: text.primary,
          backgroundColor: background.paper,
          "&:hover": {
            borderColor: alpha(primary.main, 0.4),
            backgroundColor: alpha(primary.main, 0.04),
            color: primary.main,
          },
        },
        containedPrimary: {
          backgroundColor: primary.main,
          color: "#fff",
          "&:hover": {
            backgroundColor: primary.dark,
            boxShadow: `0 6px 16px -8px ${alpha(primary.main, 0.55)}`,
          },
          "&.Mui-disabled": {
            backgroundColor: alpha(primary.main, 0.4),
            color: "#fff",
          },
        },
        sizeSmall: {
          padding: "0.3rem 0.75rem",
          fontSize: 12,
          height: 32,
        },
        sizeLarge: {
          padding: "0.6rem 1.35rem",
          fontSize: 14,
          height: 44,
        },
      },
      variants: [
        {
          props: { variant: "GreyOutlined" },
          style: {
            border: `1px solid ${divider}`,
            backgroundColor: background.paper,
            color: text.primary,
            "&:hover": {
              backgroundColor: action.hover,
              borderColor: grey[300],
            },
          },
        },
        {
          props: { variant: "GreyOutlined", size: "small" },
          style: { padding: "0.3rem 0.75rem" },
        },
        {
          props: { variant: "text" },
          style: {
            padding: "0.45rem 0.75rem",
            color: text.secondary,
            "&:hover": { backgroundColor: action.hover, color: text.primary },
          },
        },
        {
          props: { variant: "white" },
          style: {
            padding: "0.45rem 1rem",
            backgroundColor: background.paper,
            color: text.primary,
            border: `1px solid ${divider}`,
            "&:hover": { backgroundColor: action.hover },
          },
        },
        {
          props: { variant: "dashed" },
          style: {
            height: 32,
            padding: 0,
            minWidth: 32,
            maxWidth: 32,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: `1px dashed ${divider}`,
            borderRadius: "50%",
            color: text.secondary,
            "&:hover": {
              borderColor: primary.main,
              color: primary.main,
              backgroundColor: alpha(primary.main, 0.04),
            },
          },
        },
      ],
    },
    MuiButtonBase: {
      defaultProps: { disableRipple: false },
      styleOverrides: {
        root: { fontFamily: "inherit" },
      },
    },
    MuiCardHeader: {
      defaultProps: {
        titleTypographyProps: { variant: "h5" },
      },
      styleOverrides: {
        root: { padding: "1.25rem 1.5rem 0.75rem" },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: cardShadow,
          border: `1px solid ${divider}`,
          borderRadius: 14,
          backgroundImage: "none",
          backgroundColor: background.paper,
          transition: "border-color 150ms ease, box-shadow 150ms ease",
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: { minWidth: "auto", marginRight: "12px", color: text.secondary },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: "none" },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          backgroundColor: "transparent",
          boxShadow: "none",
          "&:before": { display: "none" },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          padding: 0,
          minHeight: 0,
          "&.Mui-expanded": { minHeight: "auto" },
          ".MuiAccordionSummary-content": { margin: 0 },
          ".MuiAccordionSummary-content.Mui-expanded": { margin: 0 },
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: { paddingLeft: 0, paddingRight: 0 },
      },
    },
    MuiRating: {
      styleOverrides: {
        root: { color: "#F59E0B" },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: { borderColor: "transparent" },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          "&.Mui-selected:hover": { backgroundColor: "transparent" },
        },
      },
    },
    MuiTableBody: {
      styleOverrides: {
        root: {
          "& .MuiTableRow-root:last-of-type": {
            "& .MuiTableCell-root": { borderBottom: 0 },
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: 13,
          fontWeight: 500,
          borderBottom: `1px solid ${divider}`,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          color: text.secondary,
          textTransform: "none",
          fontSize: 13,
          fontWeight: 550,
          letterSpacing: "-0.005em",
          padding: "10px 0",
          minWidth: 0,
          marginRight: "1.75rem",
          "&:last-of-type": { marginRight: 0 },
          "&:hover": { color: text.primary },
          "&.Mui-selected": { color: primary.main, fontWeight: 600 },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: 40,
          borderBottom: `1px solid ${divider}`,
          "& .MuiTabs-indicator": {
            height: 2,
            borderRadius: 2,
            backgroundColor: primary.main,
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          color: text.secondary,
          transition: "background-color 150ms ease, color 150ms ease",
          "&:hover": {
            backgroundColor: action.hover,
            color: text.primary,
          },
          "&.Mui-focusVisible": { boxShadow: focusRing },
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        root: {
          "& .MuiPopover-paper": {
            borderRadius: 12,
            border: `1px solid ${divider}`,
            backgroundImage: "none",
            backgroundColor: background.paper,
            boxShadow: popoverShadow,
          },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          border: `1px solid ${divider}`,
          padding: "4px",
          backgroundColor: background.paper,
          boxShadow: popoverShadow,
        },
        list: { padding: 0 },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: "7px 10px",
          fontSize: 13,
          fontWeight: 500,
          color: text.primary,
          minHeight: 34,
          "&.Mui-selected": {
            backgroundColor: alpha(primary.main, 0.08),
            color: primary.main,
            "&:hover": { backgroundColor: alpha(primary.main, 0.12) },
          },
          "&:hover": { backgroundColor: action.hover },
        },
      },
    },
    MuiTabPanel: {
      styleOverrides: {
        root: { padding: 0 },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          height: 4,
          borderRadius: 999,
          backgroundColor: alpha(primary.main, 0.12),
        },
        bar: { borderRadius: 999, backgroundColor: primary.main },
      },
    },
    MuiCircularProgress: {
      styleOverrides: {
        root: { color: primary.main },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          padding: 4,
          color: grey[400],
          "&.Mui-checked": { color: primary.main },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          padding: 4,
          color: grey[400],
          "&.Mui-checked": { color: primary.main },
        },
      },
    },
    MuiSwitch: {
      defaultProps: { disableRipple: true },
      styleOverrides: {
        root: {
          width: 36,
          height: 20,
          padding: 0,
          overflow: "visible",
        },
        switchBase: {
          padding: 2,
          "&.Mui-checked": {
            transform: "translateX(16px)",
            color: "#fff",
            "& + .MuiSwitch-track": {
              backgroundColor: primary.main,
              opacity: 1,
            },
            "& .MuiSwitch-thumb": { backgroundColor: "#fff" },
          },
        },
        thumb: {
          width: 16,
          height: 16,
          boxShadow: "none",
          backgroundColor: "#fff",
        },
        track: {
          borderRadius: 999,
          opacity: 1,
          backgroundColor: grey[300],
          transition: "background-color 150ms ease",
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          fontSize: 13,
          fontWeight: 500,
          "&::placeholder": { opacity: 1, color: text.disabled },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          backgroundColor: background.paper,
          transition: "box-shadow 150ms ease, border-color 150ms ease",
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: divider,
            transition: "border-color 150ms ease",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: grey[300] },
          "&.Mui-focused": { boxShadow: focusRing },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: primary.main,
            borderWidth: 1,
          },
          "&.Mui-error.Mui-focused": { boxShadow: `0 0 0 3px ${alpha(error.main, 0.22)}` },
          "&.Mui-disabled": {
            backgroundColor: alpha(text.primary, 0.02),
          },
        },
        input: { padding: "11px 13px" },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: 13,
          fontWeight: 500,
          color: text.secondary,
          "&.Mui-focused": { color: primary.main },
        },
      },
    },
    MuiTextField: {
      defaultProps: { variant: "outlined", size: "small" },
      variants: [
        {
          props: { variant: "outlined", color: "grey" },
          style: {
            "& .MuiOutlinedInput-input": { fontSize: 13, fontWeight: 500 },
            "& .MuiOutlinedInput-notchedOutline": { border: `1px solid ${divider}` },
            "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
              border: `1px solid ${grey[300]}`,
            },
            "& input::placeholder": { color: text.disabled },
          },
        },
      ],
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: { fontSize: 12, marginLeft: 2, marginTop: 6 },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 550,
          borderRadius: 999,
          letterSpacing: "-0.005em",
          height: 24,
          fontSize: 12,
        },
        label: { padding: "0 10px" },
      },
    },
    MuiTooltip: {
      defaultProps: { arrow: false },
      styleOverrides: {
        tooltip: {
          fontSize: 12,
          fontWeight: 500,
          borderRadius: 6,
          padding: "5px 8px",
          // In dark mode use a slightly lifted dark surface (lighter than paper)
          // so the tooltip doesn't glow white against dark UI.
          backgroundColor: isLight ? text.primary : grey[300],
          color: isLight ? background.paper : text.primary,
          border: isLight ? "none" : `1px solid ${divider}`,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
          border: `1px solid ${divider}`,
          backgroundColor: background.paper,
          boxShadow: dialogShadow,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: 17,
          fontWeight: 650,
          letterSpacing: "-0.015em",
          padding: "22px 26px 8px",
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: { padding: "8px 26px" },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: { padding: "16px 26px 22px" },
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          backgroundColor: alpha(isLight ? "#0C0A09" : "#000000", 0.45),
          backdropFilter: "blur(3px)",
        },
        invisible: { backgroundColor: "transparent", backdropFilter: "none" },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundImage: "none",
          backgroundColor: background.paper,
          borderColor: divider,
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: { borderColor: divider },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: `1px solid ${divider}`,
          fontSize: 13,
          padding: "10px 14px",
        },
        standardInfo: {
          backgroundColor: alpha(primary.main, 0.06),
          borderColor: alpha(primary.main, 0.22),
          color: text.primary,
          "& .MuiAlert-icon": { color: primary.main },
        },
        standardSuccess: {
          backgroundColor: alpha(success.main, 0.07),
          borderColor: alpha(success.main, 0.24),
          color: text.primary,
          "& .MuiAlert-icon": { color: success.main },
        },
        standardWarning: {
          backgroundColor: alpha("#D97706", 0.07),
          borderColor: alpha("#D97706", 0.24),
          color: text.primary,
          "& .MuiAlert-icon": { color: "#D97706" },
        },
        standardError: {
          backgroundColor: alpha(error.main, 0.06),
          borderColor: alpha(error.main, 0.24),
          color: text.primary,
          "& .MuiAlert-icon": { color: error.main },
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: { root: { "& .secondary": { opacity: 0.4 } } },
    },
    MuiPagination: {
      styleOverrides: {
        root: {
          "& .MuiPaginationItem-root": {
            borderRadius: 10,
            fontSize: 13,
            fontWeight: 500,
            color: text.secondary,
            border: "none",
            minWidth: 34,
            height: 34,
            "&:hover": { backgroundColor: action.hover, color: text.primary },
          },
          "& .MuiPaginationItem-page.Mui-selected": {
            backgroundColor: alpha(primary.main, 0.1),
            color: primary.main,
            fontWeight: 600,
            "&:hover": { backgroundColor: alpha(primary.main, 0.16) },
          },
        },
      },
    },
  };
};

export default components;
